import React, { FC, useMemo } from 'react';
import { format, isValid } from 'date-fns';
import { HighlightedTruncatedText, ReactTable } from '@td-client-frontend/common';
import { MarketingProfile, ProfileType, useDownloadAffectedGroups } from '../../../hooks/useMarketingProfile';

import { Column } from 'react-table';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { PermMode } from '../../WithPermission';
import { Permissions } from '../../../@types/Permissions';

interface MarketingProfileTabel {
  data: MarketingProfile[];
  isFetching: boolean;
  searchName: string;
}

const MarketingProfilesTable: FC<MarketingProfileTabel> = ({ data, isFetching, searchName }) => {
  const downloadAffectedGroups = useDownloadAffectedGroups();
  const { checkPerm } = useAuth();
  const dependentColumns: readonly Column<Record<string, any>>[] = useMemo(
    () => [
      {
        Header: 'Profile name',
        accessor: 'name',
        Cell: ({ row }) => {
          return (
            <HighlightedTruncatedText key={row.original.id} searchTerm={searchName} textContent={row.original.name} />
          );
        }
      },
      {
        Header: 'Base profile',
        accessor: 'default'
      },
      {
        Header: 'Assigned',
        accessor: 'affectedOrganizationsCount',
        disableSortBy: true,
        Cell: ({
          row: {
            original: { id, affectedOrganizationsCount, name }
          }
        }) => {
          return (
            <button
              onClick={() => downloadAffectedGroups(id, ProfileType.PROFILE, name)}
              className="text-blue-80 font-effra text-lg  focus:outline-none active:outline-none"
            >
              {affectedOrganizationsCount}
            </button>
          );
        }
      },
      {
        Header: 'Created by',
        accessor: 'createdBy.fullName'
      },
      {
        Header: 'Create date',
        accessor: 'createdAt',
        Cell: ({
          row: {
            original: { createdAt }
          }
        }) => {
          return createdAt && isValid(new Date(createdAt)) ? format(new Date(createdAt), 'MM/dd/yyyy') : '';
        }
      },
      {
        Header: 'Modified by',
        accessor: 'updatedBy.fullName'
      },
      {
        Header: 'Modified date',
        accessor: 'updatedAt',
        Cell: ({
          row: {
            original: { updatedAt }
          }
        }) => {
          return updatedAt && isValid(new Date(updatedAt)) ? format(new Date(updatedAt), 'MM/dd/yyyy') : '';
        }
      },
      {
        Header: 'Actions',
        disableSortBy: true,
        accessor: (row) => {
          return checkPerm(
            [
              Permissions.MarketingProfileRetentionRead,
              Permissions.MarketingProfileRegistrationRead,
              Permissions.MarketingProfilePreferencesRead,
              Permissions.MarketingProfileCommentsRead
            ],
            PermMode.ANY
          ) ? (
            <Link to={`/marketing-profile/${row.id}`}>
              <button className="flex font-effra text-primary-500 hover:text-primary-70">Manage</button>
            </Link>
          ) : (
            <></>
          );
        }
      }
    ],
    [checkPerm, downloadAffectedGroups, searchName]
  );
  return <ReactTable data={data} columns={dependentColumns} isFetching={isFetching} getSortBy manualSortBy />;
};

export default MarketingProfilesTable;
